import { BYTES_IN_GB, formatBytesToGb } from '@fd/common/biz/formatBytesToGb';
import { ProxyTypeName } from '@fd/common/biz/proxyType';
import CancelSubscriptionButton from '@/components/Settings/BillingPage/CancelSubscriptionButton';
import PaymentsTable from '@/components/Settings/BillingPage/PaymentsTable';
import UpdateSubscriptionDialogue from '@/components/Settings/BillingPage/UpdateSubscriptionDialogue';
import { Button } from '@/components/ui/Button';
import Card from '@/components/ui/Card';
import CardTitle from '@/components/ui/CardTitle';
import Flex from '@/components/ui/Flex';
import { Progress } from '@/components/ui/Progress';
import { linkClassName } from '@/components/ui/styles';
import useActiveSbSubscription, {
  useSbSubscriptions,
} from '@/hooks/queries/useSbSubscription';
import { useTrafficGbAvailableByProxyType } from '@/hooks/useTrafficAvailableByProxyType';
import { proxiesByType } from '@/model/proxyTypes';
import { formatCents } from '@/utils/formatCurrency';
import { SbSubscription } from '@fd/supabase/types';
import clsx from 'clsx';
import { useState } from 'react';
import { Link } from 'react-router';

export default function BillingPage() {
  const { data: activeSubscription } = useActiveSbSubscription();
  const { data: subscriptions } = useSbSubscriptions();
  const cancelledSubscription = subscriptions
    ? subscriptions.find((s) => new Date(s.paid_until) > new Date())
    : undefined;

  const subscription = activeSubscription ?? cancelledSubscription;
  const [isUpdateDialogOpen, setIsUpdateDialogOpen] = useState(false);
  const products = {
    residential: subscription?.residential_bytes ?? 0,
    mobile: subscription?.mobile_bytes ?? 0,
    datacenter: subscription?.datacenter_bytes ?? 0,
    isp: subscription?.isp_bytes ?? 0,
  };

  return (
    <>
      <Flex.Col className="gap-5">
        <Card>
          <Flex.Col className="gap-5">
            <CardTitle>Your current subscription</CardTitle>
            <Flex.Col className="gap-3">
              <SubscriptionCard subscription={subscription} />
              {subscription && (
                <Card className="bg-primary-background">
                  <Flex.Col className="gap-4">
                    <div className="text-sm font-semibold">Your products</div>
                    {Object.entries(products).map(
                      ([product, bytes]) =>
                        bytes > 0 && (
                          <ProductCard
                            key={product}
                            proxyType={product as ProxyTypeName}
                            bytes={bytes}
                            onUpdate={() => setIsUpdateDialogOpen(true)}
                          />
                        )
                    )}
                  </Flex.Col>
                </Card>
              )}
            </Flex.Col>
          </Flex.Col>
        </Card>
        <PaymentsTable />
      </Flex.Col>
      {subscription && (
        <UpdateSubscriptionDialogue
          isOpen={isUpdateDialogOpen}
          close={() => setIsUpdateDialogOpen(false)}
          subscription={subscription}
        />
      )}
    </>
  );
}

function SubscriptionCard(props: { subscription?: SbSubscription }) {
  const subscription = props.subscription;

  return (
    <Card className="bg-primary-background">
      {subscription && (
        <Flex.Row className="justify-between items-center">
          <Flex.Col className="gap-2">
            <div className="text-justify">
              <span className="text-2xl font-bold">
                {formatCents(subscription.amount_cents)}
              </span>{' '}
              <span className="text-secondary whitespace-nowrap">
                / Monthly
              </span>
            </div>
            <div
              className={clsx(
                'text-sm',
                subscription.active ? 'text-secondary' : 'text-red-400'
              )}
            >
              {subscription.active ? 'Renews on ' : 'Expires on '}
              {new Date(subscription.paid_until).toLocaleDateString('en-US', {
                month: 'long',
                day: 'numeric',
                year: 'numeric',
              })}
            </div>
          </Flex.Col>
          <CancelSubscriptionButton subscription={subscription} />
        </Flex.Row>
      )}
      {!subscription && (
        <Flex.Row className="justify-between items-center">
          <div>
            You don&apos;t have active subscription -{' '}
            <Link to="/proxies/buy" className={linkClassName}>
              get one here
            </Link>
            .
          </div>
        </Flex.Row>
      )}
    </Card>
  );
}

function ProductCard({
  proxyType,
  bytes,
  onUpdate,
}: {
  proxyType: ProxyTypeName;
  bytes: number;
  onUpdate: () => void;
}) {
  const proxyInfo = proxiesByType[proxyType];
  const traffic = useTrafficGbAvailableByProxyType(proxyType) ?? 0;
  const progress = Math.min(100, (traffic / (bytes / BYTES_IN_GB)) * 100);

  return (
    <Card>
      <Flex.Row className="gap-3">
        <div className="bg-proxy-icon-bg h-10 aspect-square rounded-lg flex items-center justify-center">
          {proxyInfo.icon}
        </div>
        <Flex.Col className="gap-1 sm:gap-5 w-full">
          <Flex.Row className="justify-between items-end flex-col sm:flex-row">
            <Flex.Row className="gap-3 w-full flex-wrap sm:flex-nowrap">
              <div className="w-full flex flex-col gap-3">
                <div className="flex flex-col gap-1">
                  <div className="font-medium">{proxyInfo.title}</div>
                  <p className="text-sm text-secondary">
                    Shared - Payment per GB
                  </p>
                </div>
              </div>
            </Flex.Row>
            <div className="text-sm text-secondary whitespace-normal sm:whitespace-nowrap">
              Available: {traffic.toFixed(1)} GB/{formatBytesToGb(bytes)} GB
            </div>
          </Flex.Row>
          <Progress value={progress} className="hidden sm:block" />
        </Flex.Col>
        <div className="border-l-2 border-primary-background flex items-center pl-2 ml-0 sm:pl-5 sm:ml-2">
          <Button variant="secondary" onClick={onUpdate}>
            Update
          </Button>
        </div>
      </Flex.Row>
    </Card>
  );
}
